import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useUserRole } from '@/hooks/useUserRole';
import { hasFeatureAccess } from '@/lib/permissions';

// One RLS-scoped source for every "needs you" number in the shell: the sidebar
// and section-tab badges, the notification bell and the login summary all read
// from here, so they can never disagree. Counts are real rows, nothing mocked.

const OPEN_APPROVAL_STATUSES = ['pending_review', 'escalated', 'more_evidence_requested'];
const HIGH = ['high', 'critical'];

export interface AttentionItem {
  /** Stable across refreshes; used for read/dismiss state. */
  id: string;
  type: 'risk' | 'task' | 'deadline' | 'update';
  priority: 'critical' | 'high' | 'medium';
  title: string;
  body: string;
  view: string;
  /** Changes when the underlying count changes, so a worse state re-notifies. */
  signature: string;
}

export interface Attention {
  openRisks: number;
  criticalRisks: number;
  openSignals: number;
  criticalSignals: number;
  pendingDecisions: number;
  pendingApprovals: number;
  overdueTasks: number;
  blockedTasks: number;
  /** Badge counts keyed by view. 0 means no badge. */
  byView: Record<string, number>;
  /** Badge counts keyed by nav section. */
  bySection: Record<string, { count: number; urgent: boolean }>;
  /** Items worth interrupting the user for, most urgent first. */
  items: AttentionItem[];
  isLoading: boolean;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

const head = (table: string) => supabase.from(table as never).select('id', { count: 'exact', head: true });
const countOf = (r: { count: number | null; error: { message: string } | null }) => (r.error ? 0 : r.count ?? 0);

export function useAttention(): Attention {
  const { user } = useAuth();
  const { role } = useUserRole();
  const canRisks = hasFeatureAccess(role, 'risks');
  const canSignals = hasFeatureAccess(role, 'signals');
  const canDecisions = hasFeatureAccess(role, 'decisions');
  const canApprovals = hasFeatureAccess(role, 'hitlWorkbench');
  const canWork = hasFeatureAccess(role, 'projects') || canRisks;

  // isPending (not isLoading): the query is disabled until the role resolves,
  // and a disabled query is not "loading" yet has no data. Callers must not
  // treat that state as "nothing needs attention".
  const { data, isPending: isLoading } = useQuery({
    queryKey: ['attention', user?.id, role],
    enabled: !!user && !!role,
    staleTime: 30_000,
    refetchInterval: 60_000,
    queryFn: async () => {
      const today = new Date().toISOString().slice(0, 10);
      const [risks, critRisks, sigs, critSigs, decisions, approvals, overdue, blocked] = await Promise.all([
        canRisks ? head('risk_signals').is('resolved_at', null) : null,
        canRisks ? head('risk_signals').is('resolved_at', null).in('severity', HIGH) : null,
        canSignals ? head('enterprise_signals').eq('is_resolved', false) : null,
        canSignals ? head('enterprise_signals').eq('is_resolved', false).in('severity', HIGH) : null,
        canDecisions ? head('decisions').eq('status', 'pending') : null,
        canApprovals ? head('ai_outputs').in('status', OPEN_APPROVAL_STATUSES) : null,
        canWork ? head('tasks_sync').neq('status', 'done').lt('due_date', today) : null,
        canWork ? head('tasks_sync').eq('status', 'blocked') : null,
      ]);
      const n = (r: unknown) => (r ? countOf(r as Parameters<typeof countOf>[0]) : 0);
      return {
        openRisks: n(risks), criticalRisks: n(critRisks),
        openSignals: n(sigs), criticalSignals: n(critSigs),
        pendingDecisions: n(decisions), pendingApprovals: n(approvals),
        overdueTasks: n(overdue), blockedTasks: n(blocked),
      };
    },
  });

  return useMemo<Attention>(() => {
    const c = data ?? {
      openRisks: 0, criticalRisks: 0, openSignals: 0, criticalSignals: 0,
      pendingDecisions: 0, pendingApprovals: 0, overdueTasks: 0, blockedTasks: 0,
    };
    // Tabs show the number that needs action: signals/risks that are open,
    // decisions and approvals that are waiting.
    const byView: Record<string, number> = {
      risks: c.openRisks,
      signals: c.openSignals,
      decisions: c.pendingDecisions,
      decideBoard: c.pendingDecisions + c.pendingApprovals,
      hitlWorkbench: c.pendingApprovals,
    };
    const deliverCount = c.openRisks + c.openSignals;
    const decideCount = c.pendingDecisions + c.pendingApprovals;
    const bySection = {
      deliver: { count: deliverCount, urgent: c.criticalRisks + c.criticalSignals > 0 },
      decide: { count: decideCount, urgent: false },
    };

    // What is worth interrupting someone for: things that are wrong or waiting
    // on them. Deliberately excluded: unread mail, new meetings, completed
    // milestones and generated reports. Those are information, not action, and
    // live in their own screens.
    const items: AttentionItem[] = [];
    if (c.criticalRisks + c.criticalSignals > 0) {
      const total = c.criticalRisks + c.criticalSignals;
      items.push({
        id: 'critical-risk', type: 'risk', priority: 'critical', view: 'risks', signature: String(total),
        title: `${plural(total, 'critical or high-severity risk')} open`,
        body: [c.criticalRisks && plural(c.criticalRisks, 'risk'), c.criticalSignals && plural(c.criticalSignals, 'signal')]
          .filter(Boolean).join(' and ') + ' need review.',
      });
    }
    if (c.pendingDecisions > 0) {
      items.push({
        id: 'pending-decisions', type: 'update', priority: 'high', view: 'decisions', signature: String(c.pendingDecisions),
        title: `${plural(c.pendingDecisions, 'decision')} waiting for you`,
        body: 'Pending in the decision log.',
      });
    }
    if (c.pendingApprovals > 0) {
      items.push({
        id: 'pending-approvals', type: 'update', priority: 'high', view: 'hitlWorkbench', signature: String(c.pendingApprovals),
        title: `${plural(c.pendingApprovals, 'AI output')} awaiting review`,
        body: 'Approve, correct or reject before it is used.',
      });
    }
    if (c.overdueTasks > 0) {
      items.push({
        id: 'overdue-tasks', type: 'deadline', priority: 'high', view: 'deliverHub', signature: String(c.overdueTasks),
        title: `${plural(c.overdueTasks, 'task')} overdue`,
        body: 'Past due date and not done.',
      });
    }
    if (c.blockedTasks > 0) {
      items.push({
        id: 'blocked-tasks', type: 'task', priority: 'medium', view: 'deliverHub', signature: String(c.blockedTasks),
        title: `${plural(c.blockedTasks, 'blocked task')}`,
        body: 'Work is stalled until these are unblocked.',
      });
    }

    return { ...c, byView, bySection, items, isLoading };
  }, [data, isLoading]);
}
